import type { Capability, Membership } from '@mfos/shared';
import { createContext, useContext } from 'react';

export const MembershipContext = createContext<Membership | null>(null);

/** The current user's membership in the workspace from the URL (`/w/:workspaceId`). */
export function useMembership(): Membership {
  const membership = useContext(MembershipContext);
  if (membership === null) {
    throw new Error('useMembership must be used inside a workspace route');
  }
  return membership;
}

/**
 * UI gating only (deny by default): hides controls the member may not use. The server
 * authorizes every request again.
 */
export function can(membership: Membership, capability: Capability): boolean {
  return membership.capabilities.includes(capability);
}

export function canAny(membership: Membership, capabilities: readonly Capability[]): boolean {
  return capabilities.some((capability) => membership.capabilities.includes(capability));
}

export const ADMIN_CAPABILITIES: readonly Capability[] = [
  'members.manage',
  'members.invite',
  'permissions.manage',
  'founders.manage',
  'equity.manage',
  'obligations.manage',
  'finance.manage',
  'audit.read',
  'settings.manage',
];

export function canReadObligations(membership: Membership, founderProfileId: string): boolean {
  return (
    can(membership, 'obligations.read_all') ||
    can(membership, 'obligations.manage') ||
    (can(membership, 'obligations.read_own') && membership.founderProfileId === founderProfileId)
  );
}
